import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';
import { showErrorToast } from '../../utils/toast';
import { formatDate } from '../../utils/helper';

const initialState = {
  loanApplications: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  },
  loading: false,
  error: null,
  filters: {
    search: '',
    page: 1,
    limit: 10,
    type: '',
    startDate: null,
    endDate: null,
    userId: null,
  },
};

export const fetchLoanApplication = createAsyncThunk(
  'loans/loan-applications',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().loanApplications;
      
      // Create a new URLSearchParams instance
      const queryParams = new URLSearchParams();
      
      // Add required pagination parameters
      queryParams.set('page', String(filters.page));
      queryParams.set('limit', String(filters.limit));
      
      // Add search if it exists (even if empty)
      queryParams.set('search', filters.search);
      
      // Add userId if present
      if (filters.userId) {
        queryParams.set('userId', String(filters.userId));
      }
      
      // Add type if it exists and isn't empty
      if (filters.type && filters.type !== '') {
        queryParams.set('type', filters.type);
      }
      
      // Add date range if both dates are present
      if (filters.startDate && filters.endDate) {
        queryParams.set('startDate', formatDate(filters.startDate));
        queryParams.set('endDate', formatDate(filters.endDate));
      }

      // Update URL with current filters
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      window.history.pushState({}, '', newUrl);

      const response = await axiosInstance.get(`/loan/loan-applications?${queryParams}`);
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch loan applications';
      showErrorToast(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const loanApplicationsSlice = createSlice({
  name: 'loanApplications',
  initialState,
  reducers: {
    setFilter: (state, action) => {
      // Create a new filters object with the updates
      const newFilters = { ...state.filters, ...action.payload };
      
      // Reset page to 1 if any filter other than page changes
      if (Object.keys(action.payload).some(key => 
        key !== 'page' && 
        action.payload[key] !== state.filters[key]
      )) {
        newFilters.page = 1;
      }
      
      state.filters = newFilters;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      
      // Update URL when filters are reset
      const queryParams = new URLSearchParams({
        page: String(initialState.filters.page),
        limit: String(initialState.filters.limit),
        search: initialState.filters.search,
      });
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      window.history.pushState({}, '', newUrl);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLoanApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLoanApplication.fulfilled, (state, action) => {
        state.loading = false;
        state.loanApplications = action.payload.loanApplications || [];
        state.pagination = action.payload.pagination || initialState.pagination;
      })
      .addCase(fetchLoanApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilter, resetFilters } = loanApplicationsSlice.actions;
export default loanApplicationsSlice.reducer;